import { FeatureProtectedView } from '@moniebook-web/components/molecules/feature-protected-view';
import { ProtectedView } from '@moniebook-web/components/molecules/protected-view';
import { Outlet, type RouteObject } from 'react-router';

import { StyleScope } from '@/features/shared/style-scope';

const DOCKET_FLAG = 'moniebook.pos.enableDocketsDashboard';

const RECEIPT_TEMPLATES_PERMISSIONS = ['MANAGE_RECEIPT'];
const REGISTERS_PERMISSIONS = ['MANAGE_REGISTER'];
const PAYMENT_RESTRICTION_PERMISSIONS = ['MANAGE_PAYMENT_RESTRICTION'];
const TAXES_PERMISSIONS = [
    'VIEW_TAX',
    'CREATE_TAX',
    'EDIT_TAX',
    'DELETE_TAX',
    'MANAGE_TAX',
];

export const saleSettingsRoutes: RouteObject[] = [
    {
        path: 'settings',
        element: <StyleScope name='sale-settings-ui' />,
        children: [
            {
                path: 'receipt-settings',
                element: (
                    <ProtectedView
                        accessCodeList={RECEIPT_TEMPLATES_PERMISSIONS}
                    />
                ),
                children: [
                    {
                        path: '',
                        async lazy() {
                            const { receiptSettings } = await unwrapFeatures();
                            return receiptSettings().then((m) => ({
                                Component: m.default,
                            }));
                        },
                    },
                ],
            },
            {
                path: 'register-configurations',
                element: (
                    <ProtectedView accessCodeList={REGISTERS_PERMISSIONS} />
                ),
                children: [
                    {
                        path: '',
                        async lazy() {
                            const { registerConfiguration } =
                                await unwrapFeatures();
                            return registerConfiguration().then((m) => ({
                                Component: m.default,
                            }));
                        },
                    },
                ],
            },
            {
                path: 'payment-restrictions',
                element: (
                    <ProtectedView
                        accessCodeList={PAYMENT_RESTRICTION_PERMISSIONS}>
                        <Outlet />
                    </ProtectedView>
                ),
                children: [
                    {
                        path: '',
                        async lazy() {
                            const { paymentRestrictions } =
                                await unwrapFeatures();
                            return paymentRestrictions().then((m) => ({
                                Component: m.default,
                            }));
                        },
                    },
                    {
                        path: 'create',
                        async lazy() {
                            const { createPaymentRestriction } =
                                await unwrapFeatures();
                            return createPaymentRestriction().then((m) => ({
                                Component: m.default,
                            }));
                        },
                    },
                    {
                        path: ':id/edit',
                        async lazy() {
                            const { updatePaymentRestriction } =
                                await unwrapFeatures();
                            return updatePaymentRestriction().then((m) => ({
                                Component: m.default,
                            }));
                        },
                    },
                ],
            },
            {
                path: 'dockets',
                element: (
                    <FeatureProtectedView flag={DOCKET_FLAG}>
                        <Outlet />
                    </FeatureProtectedView>
                ),
                children: [
                    {
                        path: '',
                        async lazy() {
                            const { dockets } = await unwrapFeatures();
                            return dockets().then((m) => ({
                                Component: m.default,
                            }));
                        },
                    },
                ],
            },
            {
                path: 'taxes',
                element: (
                    <ProtectedView
                        accessCodeList={TAXES_PERMISSIONS}
                        grantAccessIfAny
                    />
                ),
                children: [
                    {
                        path: '',
                        async lazy() {
                            const { taxesList } = await unwrapFeatures();
                            return taxesList().then((m) => ({
                                Component: m.default,
                            }));
                        },
                    },
                    {
                        path: '',
                        element: (
                            <ProtectedView accessCodeList={['CREATE_TAX']} />
                        ),
                        children: [
                            {
                                path: 'create',
                                async lazy() {
                                    const { createTax } =
                                        await unwrapFeatures();
                                    return createTax().then((m) => ({
                                        Component: m.default,
                                    }));
                                },
                            },
                        ],
                    },
                    {
                        path: '',
                        element: (
                            <ProtectedView accessCodeList={['EDIT_TAX']} />
                        ),
                        children: [
                            {
                                path: ':id/edit',
                                async lazy() {
                                    const { updateTax } =
                                        await unwrapFeatures();
                                    return updateTax().then((m) => ({
                                        Component: m.default,
                                    }));
                                },
                            },
                        ],
                    },
                ],
            },
        ],
    },
];

async function unwrapFeatures() {
    const features = await import('saleSettings/features');

    return features.default;
}
